import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  AppState,
  AppStateStatus,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import {useHeaderHeight} from '@react-navigation/elements';
import {useFocusEffect} from '@react-navigation/native';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import CartButton from '../../components/CartButton';
import LoadingImg from '../../components/LoadingImg';
import InStoreShoppingModal from '../../components/modals/InStoreShoppingModal';
import {MembersPriceLabel} from '../../components/ProductTile';
import {isSiteHeaderActive} from '../../components/SiteHeader/headerDenyList';
import {STAGGER} from '../../components/skeleton/constants';
import {BoneRow} from '../../components/skeleton/primitives/BoneRow';
import {BoneText} from '../../components/skeleton/primitives/BoneText';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import WishlistButton from '../../components/WishlistButton';
import {useResolvedPricing} from '../../hooks/pricing';
import useStorePriceDisplayConditions from '../../hooks/usePriceDisplayConditions';
import {useScrollCollapseJSHandler} from '../../hooks/useScrollCollapse';
import {useTabBarBottomPadding} from '../../hooks/useTabBarBottomPadding';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import NoImage from '../../images/LMG_IMAGE_NOT_AVAILABLE_transparent.png';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import rewardsTheme from '../../theme/rewardsTheme';
import apiService from '../../transport/apiService';
import {OrderLine} from '../../transport/apiServiceResponse';
import {toAUD} from '../../utils/functions';
import {buildProductTestId} from '../../utils/string';
import OrderLineItemStickers from '../cart/components/OrderLineItemStickers';

const RangeProductDetailScreen = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.PRODUCT>) => {
    const {product: productParam} = route.params || {};
    const [isLoadingProduct, setIsLoadingProduct] = useState(false);
    const {shopSelectionStore} = useStores();
    const {priceDisplayConditions} = useStorePriceDisplayConditions();
    const [isProductNotFound, setIsProductNotFound] = useState(false);
    const [imageKey, setImageKey] = useState(0);
    const [imageLoadFailed, setImageLoadFailed] = useState(false);
    const scrollViewRef = useRef<ScrollView>(null);
    const backgroundTimeRef = useRef<number>(0);
    const enrichedProductIdRef = useRef<string | null>(null);
    // DIS-1590: generation so a slower in-flight searchLines cannot setParams after
    // a newer session/store/product enrich has started.
    const enrichGenerationRef = useRef(0);
    const enrichNeedsRetryRef = useRef(false);
    // DIS-1590 review rank 1: `extra_prices.length` cannot tell "not fetched" from
    // "fetched and empty". Track successful enrich for this product+session+store key.
    const [enrichmentSettledKey, setEnrichmentSettledKey] = useState<string | null>(null);
    const [enrichRetryNonce, setEnrichRetryNonce] = useState(0);

    const {isWebDesktopScreen} = useWebScreenDimensions();
    // Native: tab bar is in normal layout flow (not absolute), so no padding needed.
    // Web desktop: no bottom tabs at all.
    // Outside ScrollCollapseProvider: modals/checkout flows, no tab bar.
    // (Mirrors the DIS-1092 / PR #216 fix for the shared hook. Kept local here
    // until that PR lands on release/6.x.)
    const rawTabBarPadding = useTabBarBottomPadding();
    const tabBarPadding = Platform.OS !== 'web' || isWebDesktopScreen ? 0 : rawTabBarPadding;

    // DIS-1902: with SiteHeader active the route header is transparent and compacts on scroll
    // (liquorStoreAppRoutes.tsx), so the content starts below it and the scroll feeds the
    // collapse context. No ref: a registered scroll-to-top button would sit over the sticky
    // price footer. Otherwise the header is static and in the layout flow, as before.
    const siteHeaderActive = isSiteHeaderActive();
    const headerHeight = useHeaderHeight();
    // Web shows scroll-to-top on this route once scrolled; registering the ref points it here.
    const {scrollHandler} = useScrollCollapseJSHandler(
      siteHeaderActive ? scrollViewRef : undefined,
    );
    const headerInset = siteHeaderActive ? {paddingTop: headerHeight} : undefined;

    // Deep link vs normal navigation handling. Not great
    // TODO: find a better way
    let product: OrderLine | null = null;
    if ((productParam as OrderLine)?.id) {
      product = productParam as OrderLine;
    }

    // DIS-1590 review fix round 1: single derivation shared by the load effect and the
    // pricing predicate below — previously duplicated verbatim in both places, which
    // risked the two halves of `enrichmentSettledId !== currentProductId` silently
    // drifting apart.
    const currentProductId =
      typeof productParam === 'string' ? productParam : (productParam as OrderLine)?.product_id;

    // `sell_price_cents` is shopper- and store-scoped. Skip key must include session +
    // store or logout / store-switch leaves member cents in route.params.
    const pricingSessionKey = `${priceDisplayConditions.isLoggedIn}:${priceDisplayConditions.isRewardsMember}:${shopSelectionStore.selectedStore?.id ?? ''}`;
    const enrichKey = currentProductId ? `${currentProductId}:${pricingSessionKey}` : null;

    useEffect(() => {
      async function load() {
        if (!currentProductId || !enrichKey) return;

        // Skip only when this exact product+session+store was successfully enriched.
        if (enrichedProductIdRef.current === enrichKey) {
          setEnrichmentSettledKey(enrichKey);
          return;
        }

        const generation = ++enrichGenerationRef.current;
        setEnrichmentSettledKey(null);

        // Fetch complete product data via searchLines for:
        // 1. Deep link / URL navigation (productParam is a string)
        // 2. In-app navigation from cart/wishlist where stickers/extra_prices may be missing
        // 3. Auth / store flip — force session-correct sell cents
        const isDeepLink = typeof productParam === 'string';
        const hasDisplayableProduct = !!(productParam as OrderLine)?.id;
        // Full-screen loader only for cold deep links — never on session re-enrich.
        if (isDeepLink && !hasDisplayableProduct) {
          setIsLoadingProduct(true);
        }

        const isReenrich =
          enrichedProductIdRef.current != null &&
          enrichedProductIdRef.current.startsWith(`${currentProductId}:`);

        try {
          const results = await apiService.searchLines({product_ids: [currentProductId]});
          if (generation !== enrichGenerationRef.current) return;

          const fetchedProduct =
            Array.isArray(results) && results.length > 0 ? (results[0] as OrderLine) : null;
          if (fetchedProduct) {
            enrichedProductIdRef.current = enrichKey;
            enrichNeedsRetryRef.current = false;
            // Re-enrich / deep link: trust MFL payload entirely so stale nav fields
            // cannot survive a session flip. First in-app open still merges for cart
            // lines that only gain stickers/extras.
            const enriched =
              isDeepLink || isReenrich
                ? fetchedProduct
                : {...(productParam as OrderLine), ...fetchedProduct};
            navigation.setParams({product: enriched});
            setEnrichmentSettledKey(enrichKey);
          } else if (isDeepLink && !hasDisplayableProduct) {
            setIsProductNotFound(true);
            enrichNeedsRetryRef.current = false;
            setEnrichmentSettledKey(enrichKey);
          } else {
            enrichNeedsRetryRef.current = true;
          }
          // In-app empty: leave unsettled + skip unset so focus can retry —
          // never latch member cents as "known" for this session.
        } catch {
          if (generation !== enrichGenerationRef.current) return;
          if (isDeepLink && !hasDisplayableProduct) {
            setIsProductNotFound(true);
            enrichNeedsRetryRef.current = false;
            setEnrichmentSettledKey(enrichKey);
          } else {
            enrichNeedsRetryRef.current = true;
          }
        } finally {
          if (generation === enrichGenerationRef.current) {
            setIsLoadingProduct(false);
          }
        }
      }
      load();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigation, enrichKey, enrichRetryNonce]);

    // Reset scroll position on product change.
    useEffect(() => {
      scrollViewRef.current?.scrollTo({y: 0, animated: false});
    }, [product?.product_id]);

    // DIS-1127: force a fresh React key (and therefore a brand-new native
    // UIImageView on iOS) every time this screen gains focus. Without this,
    // React Navigation's screen reuse (same routeKey across navigations) plus
    // iOS Fabric's UIImageView recycling can leave the recycled view's
    // texture stale after the URL swap — `onLoadStart`/`onLoadEnd` both
    // fire for the new URL, yet the presented pixels never refresh. Bumping
    // `imageKey` on focus sidesteps the Fabric recycling path entirely on
    // native, and is a no-op visual on web/Android (Image just remounts).
    useFocusEffect(
      useCallback(() => {
        setImageKey(prev => prev + 1);
        if (
          enrichNeedsRetryRef.current &&
          enrichKey &&
          enrichedProductIdRef.current !== enrichKey
        ) {
          setEnrichRetryNonce(n => n + 1);
        }
      }, [enrichKey]),
    );

    // Reset image failed-state whenever the resolved remote URL changes.
    // Tracking the URL (not product_id) matters because DIS-731 (PR #227)
    // enriches the product via searchLines AFTER first render — same
    // product_id, new links. Without this reset, a failed initial URL would
    // latch `imageLoadFailed=true` and block the enriched URL from ever
    // being tried. Also avoids a flicker loop where `onLoad` on the NoImage
    // fallback would cleared the flag and immediately retry the broken URL.
    useEffect(() => {
      setImageLoadFailed(false);
    }, [product?.links?.large_image, product?.links?.image]);

    // Handle app state changes - only refresh images if backgrounded > 5 minutes
    // This addresses potential memory pressure or stale cache after extended background
    useEffect(() => {
      const BACKGROUND_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

      const handleAppStateChange = (nextAppState: AppStateStatus) => {
        if (nextAppState === 'background' || nextAppState === 'inactive') {
          backgroundTimeRef.current = Date.now();
        } else if (nextAppState === 'active' && backgroundTimeRef.current > 0) {
          const backgroundDuration = Date.now() - backgroundTimeRef.current;
          if (backgroundDuration > BACKGROUND_THRESHOLD_MS) {
            // Force image re-render after extended background
            setImageKey(prev => prev + 1);
          }
          backgroundTimeRef.current = 0;
        }
      };

      const subscription = AppState.addEventListener('change', handleAppStateChange);
      return () => subscription?.remove();
    }, []);

    const byline =
      product?.size?.quantity && product?.size?.quantity > 1
        ? `${product?.size?.quantity} x ${product?.size?.size}${product?.size?.unit}`
        : `${product?.size?.size}${product?.size?.unit}`;
    // Sentinel for hooks when product is null (hooks must be called unconditionally)
    const emptyProduct = {
      sell_price_cents: 0,
      normal_price_cents: 0,
      stickers: [],
      extra_prices: [],
    } as OrderLine;
    const pricing = useResolvedPricing(product || emptyProduct, 'transactional');

    const testProductId = product?.product_id;

    // DIS-1590: the body and the footer answer two different questions, so they run
    // off two different predicates.
    //
    // BODY — "what does this product cost?" Product information, not a claim about
    // the reader. It shows the member price and the non-member price whenever the
    // product has both, whether or not this shopper is entitled to the member one.
    // Exception: a `private` tier is a targeted personal price, so it is shown only
    // to a shopper who is actually entitled to it — never as public information.
    // The store gate is not optional: a product can carry a public member tier in its
    // `extra_prices` while the selected store is not loyalty-enabled, and at that
    // store nobody — member or not — can get the member price. Advertising it there
    // quotes a price the shopper cannot transact at.
    // DIS-1590: the badge belongs to the same layer as MFL's own sticker — both
    // advertise that this product carries a member price at this store, which is true
    // regardless of who is looking. The footer is the layer that speaks to the
    // shopper, and it is the only one that states what they will be charged. The
    // private-tier and store gates below still apply: a targeted price is never
    // advertised, and no member price is advertised at a store that cannot honour it.
    const showBodyDualPricing = pricing.advertiseMemberPrice && pricing.nonMemberPrice !== null;

    // DIS-1590: skeleton until this product+session+store enrich succeeds.
    // Covers: missing extras at loyalty stores, and session/store re-enrich where
    // extras already exist but sell_price_cents is still the previous shopper's.
    const isPricingPending = !!enrichKey && enrichmentSettledKey !== enrichKey;

    // DIS-1590: an ineligible shopper is told what members pay, but only for a
    // `public` tier. A `private` tier is a targeted personal price — advertising it
    // to a shopper who is not entitled leaks someone else's offer and over-promises,
    // because joining will not get them that figure.
    // FOOTER — "what will I be charged?" `pricing.displayPrice` is that figure, and
    // it is the same resolver the cart line uses, so the two cannot disagree.
    //
    // A shopper who is not a signed-in member is quoted `sell_price_cents` under the
    // neutral caption "Your price". Only a signed-in member is told the member price
    // was applied. `showMemberLabel` already carries both login terms
    // (usePriceDisplayConditions.ts:23); `isIdentifiedMember` is kept explicit here
    // because the caption is a claim about the shopper, not about the price.
    const isIdentifiedMember =
      priceDisplayConditions.isLoggedIn && priceDisplayConditions.isRewardsMember;
    const footerShowsMemberApplied = isIdentifiedMember && pricing.showMemberLabel;

    // DIS-1590 (user instruction, 2026-09-02): the body is the SAME for every session —
    // member, non-member, and logged-out all read the identical price story, because the
    // story is about the product, not about the reader. The footer is the only element on
    // this screen that varies by who is looking. So the body hero is the tier figure from
    // `extra_prices`, never the shopper-scoped `sell_price_cents`, which differs per
    // session by definition and could not be identical across the three.
    //
    // Was follows the same rule: when a usable `non_member_price` tier exists,
    // `resolvePricing` derives sale/Was from that tier's normal vs sell (product RRP),
    // not from top-level sell/normal. Extras are optional — absent or malformed tiers
    // fall back to top-level comparison.
    //
    // This deliberately allows the body and the footer to print different numbers for a
    // shopper who is not an entitled member: the body advertises, the footer quotes. It
    // reverses the audit's rank-2 finding (`ea872504b`), which required the body to follow
    // the footer's figure. Confirmed by the user before the change.
    //
    // The body is NOT identical when the member tier is `private`: `showBodyDualPricing`
    // still withholds a targeted price from a shopper not entitled to it. That gate is
    // load-bearing and measured — MFL does not enforce it server-side.
    const bodyHeroPrice = showBodyDualPricing ? pricing.memberPrice! : pricing.displayPrice;

    const abvValue = product?.alcohol_by_volume_basis_points
      ? `${product.alcohol_by_volume_basis_points / 100}%`
      : null;

    const attributeRow = (label: string, value: string, testIDSuffix: string) => (
      <View style={styles.attributeRow} key={testIDSuffix}>
        <Text style={styles.attributeLabel}>{label}</Text>
        <Text testID={`${testProductId}-${testIDSuffix}`} style={styles.attributeValue}>
          {value}
        </Text>
      </View>
    );

    const productAttributes = () => {
      const rows = [
        product?.place_of_origin?.country
          ? attributeRow('Country', product.place_of_origin.country, 'country')
          : null,
        product?.place_of_origin?.region
          ? attributeRow('Region', product.place_of_origin.region, 'region')
          : null,
        abvValue ? attributeRow('ABV', abvValue, 'abv') : null,
        product?.brand_name ? attributeRow('Brand', product.brand_name, 'brand') : null,
      ].filter(Boolean);

      return rows.length ? <View style={styles.attributeTable}>{rows}</View> : null;
    };

    if (isProductNotFound) {
      return (
        <ThemeProvider theme={rewardsTheme}>
          <ContentView testID="shop-product-detail-screen" parentStyle={headerInset}>
            <Text>Product not found</Text>
          </ContentView>
        </ThemeProvider>
      );
    }

    // pricing already resolved above via useResolvedPricing

    const handleImageError = () => {
      setImageLoadFailed(true);
    };

    // Resolve a single source URL once per render. Prefer large_image; fall
    // back to image when large is absent; fall back to NoImage when there's
    // no remote URL OR a prior onError marked the load as failed.
    const remoteImageUrl = product?.links?.large_image || product?.links?.image;
    const imageSource = !remoteImageUrl || imageLoadFailed ? NoImage : {uri: remoteImageUrl};

    // ONE `<Image>` element regardless of source. Swapping the `source` prop
    // reactively — instead of unmounting/remounting sibling conditional
    // branches — avoids an iOS Fabric bug where the new native view
    // initializes but never starts loading the remote URI.
    //
    // Include the URL itself in the `key` so that when DIS-731 (PR #227)
    // enriches the product via searchLines and replaces `product.links` with
    // a new URL, React unmounts the old Image and mounts a fresh one. iOS
    // Fabric's Image view has a separate bug where changing `source.uri` on
    // an already-mounted native view sometimes keeps the stale image or
    // never completes the new load (Android/web re-fetch correctly). Forcing
    // remount on URL change sidesteps that — and only fires when the URL
    // genuinely changes, so no extra churn on scroll/re-render.
    const imageKeyValue = `${remoteImageUrl ?? 'noimage'}-${imageKey}`;
    const renderProductImage = () => (
      <View style={styles.productImageHolder} testID={`${testProductId}-image-holder`}>
        <View style={styles.membersPriceLabelContainer}>
          <MembersPriceLabel showLabel={showBodyDualPricing} labelText={pricing.memberLabelText} />
        </View>
        <Image
          key={imageKeyValue}
          source={imageSource}
          style={styles.productImage}
          resizeMode="contain"
          onError={handleImageError}
        />
        {product ? <OrderLineItemStickers item={product} size="large" isProductScreen /> : null}
      </View>
    );

    const renderProductDescription = () => (
      <View
        testID={`${testProductId}-attributes`}
        style={isWebDesktopScreen ? styles.productDescriptionMainDesktop : {}}>
        <Text style={styles.headline}>{product?.name}</Text>
        <Text testID={`${testProductId}-quantity`} style={styles.bylineUnderName}>
          {byline}
        </Text>

        {isPricingPending ? (
          <View style={styles.priceBlock} testID={`${testProductId}-price-skeleton`}>
            {/* Mirror settled layout: hero price row + at least one attribute row
                (Non-member / Was / both). */}
            <View testID={`${testProductId}-price-skeleton-primary`}>
              <BoneText variant="price" width={180} animationDelay={STAGGER.delay(0)} />
            </View>
            <View testID={`${testProductId}-price-skeleton-meta`}>
              <BoneRow
                leftWidth={0.42}
                rightWidth={0.22}
                height={12}
                animationDelay={STAGGER.delay(1)}
                style={styles.priceSkeletonMetaRow}
              />
            </View>
          </View>
        ) : (
          <View style={styles.priceBlock}>
            {showBodyDualPricing ? (
              <View style={styles.priceDetailRow}>
                <Text style={styles.memberPriceLabel}>Member</Text>
                <View style={styles.priceDetailValueGroup}>
                  <Text testID={`${testProductId}-price`} style={styles.memberPriceValue}>
                    {toAUD(bodyHeroPrice)}
                  </Text>
                  <Text style={styles.priceUnit}> each</Text>
                </View>
              </View>
            ) : (
              <View style={styles.priceLineLeft}>
                <Text testID={`${testProductId}-price`} style={styles.price}>
                  {toAUD(bodyHeroPrice)}
                </Text>
                <Text style={styles.priceUnit}> each</Text>
              </View>
            )}

            {showBodyDualPricing ? (
              <View style={styles.priceDetailRow}>
                <Text style={styles.priceDetailLabel}>Non-member price</Text>
                <View style={styles.priceDetailValueGroup}>
                  <Text testID={`${testProductId}-nonmember-price`} style={styles.priceDetailValue}>
                    {toAUD(pricing.nonMemberPrice!)}
                  </Text>
                  <Text style={styles.priceDetailUnit}> each</Text>
                </View>
              </View>
            ) : null}

            {pricing.isOnSale ? (
              <View style={styles.priceDetailRow}>
                <Text style={styles.priceDetailLabel}>Was</Text>
                <Text testID={`${testProductId}-was-price`} style={styles.wasValue}>
                  {toAUD(pricing.wasPrice!)}
                </Text>
              </View>
            ) : null}
          </View>
        )}

        {productAttributes()}

        <Text testID={`${testProductId}-description`} style={styles.copy}>
          {product?.description}
        </Text>
      </View>
    );

    if (isLoadingProduct || !product) {
      return (
        <ThemeProvider theme={rewardsTheme}>
          <ContentView testID="shop-product-detail-screen" parentStyle={headerInset}>
            <LoadingImg />
          </ContentView>
        </ThemeProvider>
      );
    }

    const productTestId = buildProductTestId(product.name, 'pdp');

    // Images are a bit dicky as we're using this component for the range products
    // and the cart products. Cart products only have .image, while range has large.
    return (
      <ThemeProvider theme={rewardsTheme}>
        <ContentView
          testID="shop-product-detail-screen"
          parentStyle={{paddingBottom: tabBarPadding}}>
          <ScrollView
            ref={scrollViewRef}
            testID="shop-product-detail-screen-scrollview"
            onScroll={siteHeaderActive ? scrollHandler : undefined}
            scrollEventThrottle={16}
            contentContainerStyle={headerInset}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}>
            <View
              testID="shop-product-detail-screen-scrollview-content"
              style={isWebDesktopScreen ? styles.mainWebDesktop : {}}>
              {renderProductImage()}
              {renderProductDescription()}
            </View>
          </ScrollView>

          <View style={[styles.stickyFooter, {justifyContent: 'space-between'}]}>
            <View style={styles.priceLineFooter}>
              {isPricingPending ? (
                <BoneText variant="price" width={110} testID={`${productTestId}-price-skeleton`} />
              ) : (
                <View style={styles.footerPriceBlock}>
                  {/* DIS-1590 (user instruction): the footer is the ONLY element on this
                      screen that varies by session. The caption states who the reader is;
                      the figure below it is `displayPrice`, MFL's shopper-scoped
                      `sell_price_cents` and the same resolver call the cart line makes, so
                      it is always the price this shopper will actually be charged. */}
                  <Text
                    style={[
                      styles.footerPriceTag,
                      footerShowsMemberApplied ? styles.footerPriceTagMember : null,
                    ]}>
                    {footerShowsMemberApplied ? 'MEMBER PRICE' : 'YOUR PRICE'}
                  </Text>
                  <View style={styles.footerPriceRow}>
                    <Text testID={`${productTestId}-price`} style={styles.priceFooter}>
                      {toAUD(pricing.displayPrice)}
                    </Text>
                    <Text style={styles.priceUnitFooter}> ea</Text>
                  </View>
                </View>
              )}
            </View>

            <View style={styles.footerButtonHolder}>
              <WishlistButton item={product} testContext="pdp" />
            </View>
            <View style={styles.cartButtonContainer}>
              <CartButton item={product} testContext="pdp" />
            </View>
          </View>
          <InStoreShoppingModal />
        </ContentView>
      </ThemeProvider>
    );
  },
);

export default RangeProductDetailScreen;

const styles = StyleSheet.create({
  productDescriptionMainDesktop: {
    flex: 1,
  },
  mainWebDesktop: {
    flex: 1,
    flexDirection: 'row',
  },
  stickyFooter: {
    borderTopWidth: 1,
    borderTopColor: theme.colour.border.tile,
    // DIS-1590: the caption now sits above the figure, so the footer needs two lines of
    // room. 60 remains the floor.
    minHeight: 60,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  priceLineFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    // DIS-1590: the price is the one thing in this footer that must never be shrunk or
    // overlapped — flexbox will otherwise squeeze it below its content width once the
    // member panel joins the row, and the "ea" unit renders under the panel.
    flexShrink: 0,
  },
  priceLineFooterRight: {
    justifyContent: 'flex-end',
  },
  cartButtonContainer: {
    minWidth: 120,
    maxWidth: 150,
  },
  footerButtonHolder: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  svg: {},
  priceFooter: {
    fontSize: 24,
    fontWeight: '500',
    color: theme.colour.text.heading.primary,
  },
  priceUnitFooter: {
    color: theme.colour.text.heading.primary,
  },
  // DIS-1590: the price block is one vertical story — what you pay (hero), what it
  // saves you, then the comparison figures as label/value rows.
  bylineUnderName: {
    fontSize: theme.size.text.caption,
    color: theme.colour.text.tile.byline,
    marginBottom: 12,
  },
  priceBlock: {
    marginBottom: 16,
  },
  priceSkeletonMetaRow: {
    marginTop: 8,
  },
  // DIS-1590: the price block keeps the pre-PR price-line treatment — grey, the original
  // sizes and weights, no accent colour. `theme.colour.text.tile.byline` is the brand
  // token for this grey; the original literal `'grey'` was #808080, the token is #666666.
  priceLineLeft: {
    flexDirection: 'row',
    alignItems: 'baseline',
    alignSelf: 'stretch',
  },
  price: {
    fontSize: theme.size.text.heading1,
    fontWeight: '500',
    color: theme.colour.text.tile.byline,
  },
  // The one departure from the original, by request: the member figure carries a heavier
  // weight than the original price line's '500'.
  memberPriceValue: {
    fontSize: theme.size.text.heading1,
    fontWeight: 'bold',
    color: theme.colour.text.tile.byline,
  },
  priceUnit: {
    color: theme.colour.text.tile.byline,
    fontWeight: 'bold',
    alignSelf: 'flex-end',
  },
  priceDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginTop: 8,
  },
  // The Member row keeps the body size and is bold on both halves; the comparison rows
  // sit one step down at `body2`.
  memberPriceLabel: {
    fontWeight: 'bold',
    color: theme.colour.text.tile.byline,
  },
  priceDetailLabel: {
    fontSize: theme.size.text.body2,
    color: theme.colour.text.tile.byline,
  },
  priceDetailValueGroup: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  priceDetailValue: {
    fontSize: theme.size.text.body2,
    color: theme.colour.text.tile.byline,
  },
  // DIS-1590: the unit follows the figure it qualifies. The member row's ` each` is bold
  // because its figure is; this row's figure is not, so neither is its unit. The original
  // `priceUnit` was unconditionally bold, which left `$59.99` at 400 beside ` each` at 700.
  priceDetailUnit: {
    fontSize: theme.size.text.body2,
    color: theme.colour.text.tile.byline,
    alignSelf: 'flex-end',
  },
  wasValue: {
    fontSize: theme.size.text.body2,
    color: theme.colour.text.tile.byline,
    textDecorationLine: 'line-through',
  },
  attributeTable: {
    borderTopWidth: 1,
    borderTopColor: theme.colour.border.tile,
    paddingTop: 12,
  },
  attributeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  attributeLabel: {
    fontSize: theme.size.text.caption,
    color: theme.colour.text.tile.byline,
  },
  attributeValue: {
    fontSize: theme.size.text.caption,
    // DIS-1590: Country / Region / ABV / Brand are body content, not headings. The
    // heading token is a brand accent (blue on BM and SNS), which made these values read
    // as links beside the real ones in the description.
    color: theme.colour.text.content.primary,
  },
  footerPriceBlock: {
    justifyContent: 'center',
    flexShrink: 0,
  },
  footerPriceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  footerPriceTag: {
    fontSize: 12,
    fontWeight: 'bold',
    color: theme.colour.text.tile.byline,
  },
  footerPriceTagMember: {
    color: theme.colour.text.tile.price,
  },
  productImageHolder: {
    alignItems: 'center',
    flex: 1,
    marginBottom: 5,
    position: 'relative',
  },
  membersPriceLabelContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
  },
  productImage: {
    width: '90%',
    height: 450,
    borderRadius: 4,
  },
  headline: {
    fontSize: 20,
    fontWeight: 'bold',
    marginVertical: 8,
    color: theme.colour.text.heading.primary,
  },
  copy: {
    fontSize: 16,
    marginTop: 12,
    marginBottom: 16,
    color: 'black',
  },
});
